import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getFormatPainterControl } from 'owa-compose-formatcontrols/lib/components/formatPainter';
import { getPasteCutCopyControl } from 'owa-compose-formatcontrols/lib/components/pasteCutCopy';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlId } from 'owa-ribbon-ids';
import { ActionControlId, MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import * as SharedControlDef from '../../../sharedControls/composeSharedControlDefinitions';
import * as ControlDef from './slrMessageTabControlDefinitions';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getSLRMessageTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const popoutComposeViewState: ComposeViewState | undefined = props.popoutComposeViewState;
        const viewState: ComposeViewState | undefined =
            popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const defaultRibbonStyles = getDefaultRibbonStyles();
        const editorId = viewState.editorId;
        const formatViewState = viewState.ribbon.formatViewState;

        switch (controlId) {
            case ActionControlId.Sensitivity:
                return ControlDef.getSensitivityControl(props);
            case MailRibbonControlId.AddressBook:
                return ControlDef.getAddressBookControl(popoutComposeViewState, props.targetWindow);
            case MailRibbonControlId.AttachFile:
                return SharedControlDef.getAttachFileControl(
                    props.attachmentPickers!.addFilesAsAttachments!, // Cannot be undefined, because shouldShowRibbonControl checks for this
                    popoutComposeViewState,
                    MailRibbonTabId.Tab_Message
                );
            case MailRibbonControlId.AttachSig:
                return SharedControlDef.getAttachSigControl(
                    popoutComposeViewState,
                    MailRibbonTabId.Tab_Message
                );
            case MailRibbonControlId.AssignPolicy:
                return ControlDef.getAssignPolicyControl(popoutComposeViewState);
            case MailRibbonControlId.CheckNames:
                return ControlDef.getCheckNamesControl(popoutComposeViewState);
            case MailRibbonControlId.Dictate:
                return ControlDef.getDictateControl(popoutComposeViewState);
            case MailRibbonControlId.Encrypt:
                return ControlDef.getEncryptControl(popoutComposeViewState, props.targetWindow);
            case MailRibbonControlId.Encrypt_SingleButton:
                return ControlDef.getEncryptSingleButtonControl(
                    popoutComposeViewState,
                    props.targetWindow
                );
            case MailRibbonControlId.FlagUnflag:
                return ControlDef.getFlagUnflagControl(popoutComposeViewState);
            case MailRibbonControlId.FormatPainter:
                return getFormatPainterControl(
                    editorId,
                    formatViewState,
                    defaultRibbonStyles,
                    MailRibbonTabId.Tab_Message
                );
            case MailRibbonControlId.HighImportance:
                return ControlDef.getHighImportanceControl(popoutComposeViewState);
            case MailRibbonControlId.ImmersiveReader:
                return ControlDef.getImmersiveReaderControl(popoutComposeViewState);
            case MailRibbonControlId.LowImportance:
                return ControlDef.getLowImportanceControl(popoutComposeViewState);
            case MailRibbonControlId.Paste:
                return getPasteCutCopyControl(
                    viewState,
                    defaultRibbonStyles,
                    MailRibbonTabId.Tab_Message
                );
            case MailRibbonControlId.ReadAloud:
                return ControlDef.getReadAloudControl(popoutComposeViewState);
            case MailRibbonControlId.RibbonCustomizer:
                return ControlDef.getRibbonCustomizerControl();
            case MailRibbonControlId.Save:
                return ControlDef.getSaveControl(popoutComposeViewState);
            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getSLRMessageTabControlDefinition'
                );
        }
    }
);
